import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, type TenantRecord } from '@/lib/api';
import { useTenant } from '@/hooks/useTenant';

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

function createSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function extractBrandColor(settings: Record<string, unknown> | undefined): string {
  if (!settings) {
    return '';
  }

  const color = settings.brandColor;
  return typeof color === 'string' && HEX_COLOR_REGEX.test(color) ? color : '';
}

export function AdminTenantsPage() {
  const queryClient = useQueryClient();
  const { isAdmin, setTenantList } = useTenant();

  const [search, setSearch] = useState('');
  const [createName, setCreateName] = useState('');
  const [createSlugInput, setCreateSlugInput] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editBrandColor, setEditBrandColor] = useState('');

  const tenantsQuery = useQuery({
    queryKey: ['admin-tenants', search],
    queryFn: () =>
      api.listTenants({
        page: 1,
        pageSize: 100,
        search: search || undefined,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    enabled: isAdmin,
  });

  useEffect(() => {
    if (!tenantsQuery.data) {
      return;
    }

    setTenantList(tenantsQuery.data.data);
  }, [tenantsQuery.data, setTenantList]);

  const createMutation = useMutation({
    mutationFn: (payload: { name: string; slug: string }) => api.createTenant(payload),
    onSuccess: () => {
      toast.success('Tenant created');
      setCreateName('');
      setCreateSlugInput('');
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to create tenant');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      name,
      slug,
      settings,
    }: {
      id: string;
      name: string;
      slug: string;
      settings?: Record<string, unknown>;
    }) => api.updateTenant(id, { name, slug, settings }),
    onSuccess: () => {
      toast.success('Tenant updated');
      setEditingId(null);
      setEditName('');
      setEditSlug('');
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to update tenant');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (tenantId: string) => api.deactivateTenant(tenantId),
    onSuccess: () => {
      toast.success('Tenant deactivated');
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to deactivate tenant');
    },
  });

  const createSlugValue = useMemo(() => {
    if (createSlugInput.trim()) {
      return createSlug(createSlugInput);
    }

    return createSlug(createName);
  }, [createName, createSlugInput]);

  const startEdit = (tenant: TenantRecord) => {
    setEditingId(tenant.id);
    setEditName(tenant.name);
    setEditSlug(tenant.slug);
    setEditBrandColor(extractBrandColor(tenant.settings));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditSlug('');
    setEditBrandColor('');
  };

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();

    const name = createName.trim();
    const slug = createSlugValue;

    if (!name || !slug) {
      toast.error('Name and slug are required');
      return;
    }

    createMutation.mutate({ name, slug });
  };

  const handleSaveEdit = () => {
    if (!editingId) {
      return;
    }

    const tenant = tenantsQuery.data?.data.find(item => item.id === editingId);
    if (!tenant) {
      toast.error('Tenant not found in current list');
      return;
    }

    const name = editName.trim();
    const slug = createSlug(editSlug);
    const brandColor = editBrandColor.trim();

    if (!name || !slug) {
      toast.error('Name and slug are required');
      return;
    }

    if (brandColor && !HEX_COLOR_REGEX.test(brandColor)) {
      toast.error('Brand color must be a hex value like #3B82F6');
      return;
    }

    const nextSettings = { ...(tenant.settings ?? {}) };

    if (brandColor) {
      nextSettings.brandColor = brandColor;
    } else {
      delete nextSettings.brandColor;
    }

    updateMutation.mutate({ id: editingId, name, slug, settings: nextSettings });
  };

  const handleDeactivate = (tenant: TenantRecord) => {
    if (!tenant.isActive) {
      return;
    }

    if (!window.confirm(`Deactivate tenant "${tenant.name}"?`)) {
      return;
    }

    deactivateMutation.mutate(tenant.id);
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Tenants</h1>
          <p className="text-muted-foreground">Admin access is required to view this page.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tenants</h1>
          <p className="text-muted-foreground mt-1">Create, edit, and deactivate tenants</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Create Tenant</h2>
          <form className="grid gap-3 md:grid-cols-4" onSubmit={handleCreate}>
            <Input
              placeholder="Tenant name"
              value={createName}
              onChange={event => setCreateName(event.target.value)}
              className="md:col-span-2"
            />
            <Input
              placeholder="Slug (optional)"
              value={createSlugInput}
              onChange={event => setCreateSlugInput(event.target.value)}
            />
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">
            Generated slug: {createSlugValue || 'n/a'}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-foreground">Tenants</h2>
            <Input
              placeholder="Search tenants..."
              value={search}
              onChange={event => setSearch(event.target.value)}
              className="sm:max-w-xs"
            />
          </div>

          {tenantsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading tenants...</p>
          ) : tenantsQuery.isError ? (
            <p className="text-sm text-red-600">
              {tenantsQuery.error instanceof Error
                ? tenantsQuery.error.message
                : 'Failed to load tenants'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Slug</th>
                    <th className="py-2 pr-3">Brand color</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantsQuery.data?.data.map(tenant => {
                    const isEditing = editingId === tenant.id;
                    const tenantBrandColor = extractBrandColor(tenant.settings);

                    return (
                      <tr key={tenant.id} className="border-b border-border last:border-b-0">
                        <td className="py-2 pr-3 align-top">
                          {isEditing ? (
                            <Input
                              value={editName}
                              onChange={event => setEditName(event.target.value)}
                            />
                          ) : (
                            <span className="font-medium text-foreground">{tenant.name}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 align-top">
                          {isEditing ? (
                            <Input
                              value={editSlug}
                              onChange={event => setEditSlug(event.target.value)}
                            />
                          ) : (
                            <span className="text-muted-foreground">{tenant.slug}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 align-top">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editBrandColor}
                                onChange={event => setEditBrandColor(event.target.value)}
                                placeholder="#3B82F6"
                                className="w-28 font-mono text-xs"
                              />
                              <span
                                className="inline-block h-5 w-5 rounded-full border border-border"
                                style={{
                                  backgroundColor: HEX_COLOR_REGEX.test(editBrandColor)
                                    ? editBrandColor
                                    : '#cccccc',
                                }}
                              />
                            </div>
                          ) : tenantBrandColor ? (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <span
                                className="inline-block h-4 w-4 rounded-full border border-border"
                                style={{ backgroundColor: tenantBrandColor }}
                              />
                              <span className="font-mono text-xs">{tenantBrandColor}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 align-top">
                          <span
                            className={tenant.isActive ? 'text-green-600' : 'text-muted-foreground'}
                          >
                            {tenant.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-2 align-top">
                          <div className="flex flex-wrap gap-2">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={handleSaveEdit}
                                  disabled={updateMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit}>
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => startEdit(tenant)}>
                                Edit
                              </Button>
                            )}

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeactivate(tenant)}
                              disabled={!tenant.isActive || deactivateMutation.isPending}
                            >
                              Deactivate
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
